import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, acceptSession } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("password");        // "password" | "code"
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  // Every login request made here ({request_id, poll_secret, until}). All are
  // watched: a rate-limited resend returns a stand-in, and the earlier one still counts.
  const pending = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // ?next=/app/... from a used/expired one-time link — only in-app paths.
  const nextPath = (() => {
    const n = searchParams.get("next") || "";
    return n.startsWith("/app") && !n.startsWith("//") ? n : "/app/dashboard";
  })();

  const requestCode = async () => {
    if (!email.trim()) return toast.error("Enter your email first");
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { email: email.trim() });
      pending.current = [...pending.current, { request_id: r.data.request_id, poll_secret: r.data.poll_secret,
                                               until: Date.now() + (r.data.expires_in || 600) * 1000 }].slice(-6);
      setCodeSent(true);
      setCooldown(60);
      toast.success(r.data?.message || "Code sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send a code");
    } finally { setBusy(false); }
  };

  // Wait for "Approve login" on WhatsApp or the email link — no code typing needed.
  useEffect(() => {
    if (mode !== "code" || !codeSent) return;
    let stopped = false;
    const tick = async () => {
      const live = pending.current.filter((p) => Date.now() < p.until);
      if (stopped || !live.length) return;
      for (const p of live) {
        try {
          const r = await api.post("/auth/otp/poll", { request_id: p.request_id, poll_secret: p.poll_secret });
          if (stopped) return;
          if (r.data.status === "approved") {
            pending.current = [];
            acceptSession(r.data);
            toast.success("Sign-in approved");
            nav(nextPath, { replace: true });
            return;
          }
          if (r.data.status === "denied") {
            pending.current = [];
            toast.error("This sign-in was declined.");
            setCodeSent(false); setCode("");
            return;
          }
        } catch { /* keep waiting */ }
      }
      setTimeout(tick, 2500);
    };
    const t = setTimeout(tick, 2500);
    return () => { stopped = true; clearTimeout(t); };
  }, [mode, codeSent]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { email: email.trim(), code: code.trim() });
      acceptSession(r.data);
      toast.success("Signed in");
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn't work");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      const planId = searchParams.get("plan");
      if (planId) {
        nav(`/pricing?plan=${planId}`);
      } else {
        nav(nextPath, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--sw-bg)]">
      <div className="hidden lg:block relative overflow-hidden border-r border-slate-200">
        <img
          src="https://images.unsplash.com/photo-1770009971150-f50bc7d373a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGdlb21ldHJpYyUyMGxpZ2h0JTIwYXJjaGl0ZWN0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc2ODE4NTJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-white/30 to-transparent" />
        <div className="relative z-10 p-10 flex flex-col h-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-slate-900 grid place-items-center">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="font-heading text-lg font-semibold">Brainydo</div>
          </div>
          <div className="mt-auto bg-white/95 backdrop-blur p-6 border border-slate-200 max-w-md">
            <div className="label-eyebrow">Your WhatsApp Task & Follow-up Engine</div>
            <h1 className="font-heading text-3xl font-semibold mt-3 leading-tight">
              Never miss a client commitment, follow-up, or quotation again.
            </h1>
            <p className="text-slate-600 mt-3 text-[14px]">
              Brainydo turns business WhatsApp chatter into a triaged action board — auto-categorised, prioritised, and review-gated.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={mode === "password" ? submit : (codeSent ? verifyCode : (e) => { e.preventDefault(); requestCode(); })} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-slate-900 grid place-items-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="font-heading font-semibold">Brainydo</div>
          </div>
          <div className="label-eyebrow mb-2">Sign in</div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Welcome back.</h2>
          <p className="text-slate-600 mt-2 text-[14px]">Continue triaging your team's WhatsApp.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="label-eyebrow">Email</label>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-sm bg-white outline-none focus:border-slate-900 transition text-[14px]"
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>
            {mode === "password" ? (
              <>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label-eyebrow">Password</label>
                    <Link
                      to="/forgot-password"
                      className="text-[11px] text-slate-600 hover:text-slate-900 underline underline-offset-2"
                      data-testid="link-forgot-password"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-sm bg-white outline-none focus:border-slate-900 transition text-[14px]"
                    data-testid="login-password-input"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  type="submit" disabled={busy}
                  className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium text-[14px] rounded-sm transition disabled:opacity-60"
                  data-testid="login-submit-btn"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </>
            ) : !codeSent ? (
              <button
                type="submit" disabled={busy}
                className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium text-[14px] rounded-sm transition disabled:opacity-60"
                data-testid="otp-request-btn"
              >
                {busy ? "Sending…" : "Send me a login code"}
              </button>
            ) : (
              <>
                <div>
                  <label className="label-eyebrow">Login code</label>
                  <input
                    inputMode="numeric" autoComplete="one-time-code" required autoFocus maxLength={6}
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="mt-1.5 w-full h-12 px-3 border border-slate-200 rounded-sm bg-white outline-none focus:border-slate-900 transition text-[22px] tracking-[0.4em] font-semibold text-center"
                    data-testid="otp-code-input"
                    placeholder="••••••"
                  />
                  <p className="text-[12px] text-slate-500 mt-1.5">Sent to your email and WhatsApp (if verified). Valid for 10 minutes.</p>
                  <div className="mt-3 flex items-center gap-2 p-2.5 rounded-sm bg-emerald-50 border border-emerald-200 text-[12.5px] text-emerald-800" data-testid="otp-waiting">
                    <Loader2 size={14} className="animate-spin shrink-0" />
                    <span>Or just tap <b>Approve login</b> on WhatsApp, or <b>Approve sign-in</b> in the email — this page signs you in automatically.</span>
                  </div>
                </div>
                <button
                  type="submit" disabled={busy || code.length !== 6}
                  className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium text-[14px] rounded-sm transition disabled:opacity-60"
                  data-testid="otp-verify-btn"
                >
                  {busy ? "Checking…" : "Sign in"}
                </button>
                <button type="button" onClick={requestCode} disabled={busy || cooldown > 0}
                  className="w-full text-[12.5px] text-slate-600 hover:text-slate-900 disabled:text-slate-400">
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                </button>
              </>
            )}
            <button type="button"
              onClick={() => { setMode(mode === "password" ? "code" : "password"); setCodeSent(false); setCode(""); }}
              className="w-full h-10 border border-slate-200 hover:border-slate-900 text-slate-800 text-[13.5px] rounded-sm transition"
              data-testid="login-mode-toggle">
              {mode === "password" ? "Sign in with a login code instead" : "Sign in with password instead"}
            </button>
            <p className="text-[13px] text-slate-600 pt-2">
              No account?{" "}
              <Link to="/register" className="text-slate-900 underline underline-offset-2" data-testid="link-register">
                Create one
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
